import { X, XSquare } from 'lucide-react';
import {
  DockviewReact,
  themeAbyss,
  type DockviewReadyEvent,
  type IDockviewPanelHeaderProps,
  type IDockviewPanelProps,
} from 'dockview-react';
import 'dockview-react/dist/styles/dockview.css';

import { Tip } from '@/components/ui/overlays';

import { setDockApi } from '../refs';
import { workspace } from '../controller';
import { startPanelDrag } from '../drag';
import { useWorkspaceStore } from '../store';
import { bandExcess, tabDragMode, targetTabIndex } from '../tab-reorder';
import { PanelBody } from './PanelBody';

/**
 * Custom tab (Obsidian-style: a tab IS a live editor you can shove around):
 * - a click activates the panel;
 * - a small mostly-horizontal drag that stays WITHIN the tab-strip band reorders
 *   the tab in place (drives Dockview's own order + the store's `tabOrder`);
 * - a drag that pulls OUT of the strip vertically detaches it into a floating
 *   window (the pre-existing behavior, unchanged).
 *
 * Game-engine loop (DOCK_CONTRACT.md §7): the tab band and every other tab's
 * centre are measured ONCE at pointer-down. During the drag the tab follows the
 * pointer by a direct CSS `transform` and the drop slot is resolved by cheap
 * math against those precomputed centres — no per-frame `getBoundingClientRect`,
 * no live Dockview `moveTo`, no per-frame store write. An insertion caret shows
 * where it will land; the reorder is committed exactly once, on drop. Because
 * nothing reorders mid-drag, the precomputed centres never go stale.
 *
 * Escape / pointer-cancel abort cleanly (nothing was committed yet); native
 * Dockview DnD stays disabled (see DockLayer).
 */
function CustomTab(props: IDockviewPanelHeaderProps) {
  const panelId = props.api.id;
  const title = useWorkspaceStore((s) => s.panels[panelId]?.title) ?? panelId;

  const onPointerDown = (e: React.PointerEvent) => {
    if ((e.target as HTMLElement).closest('[data-no-drag]')) return;
    const tabEl = e.currentTarget as HTMLElement;
    // ── Precompute ALL static geometry ONCE. The strip band is this tab's own
    //    vertical extent; leaving it detaches.
    const bandRect = tabEl.getBoundingClientRect();
    const bandTop = bandRect.top;
    const bandBottom = bandRect.bottom;
    const startX = e.clientX;
    const originalIndex = workspace.tabbedOrder().indexOf(panelId);
    // The OTHER tabs (visual order) + their horizontal centres — measured once.
    // Valid for the whole drag because we never reorder until drop.
    const others = [...document.querySelectorAll<HTMLElement>('.wp-tab[data-panel-id]')].filter(
      (t) => t.dataset.panelId !== panelId,
    );
    const otherCentres = others.map((t) => {
      const r = t.getBoundingClientRect();
      return r.left + r.width / 2;
    });

    let mode: 'idle' | 'reorder' | 'detach' = 'idle';
    let dropIndex = originalIndex;
    let caretEl: HTMLElement | null = null;

    // The element focused when the drag began — typically the editor the user was
    // typing in. Reordering re-parents the panel's DOM (Dockview moveTo), which
    // blurs whatever was focused inside it; the CONTENT survives (never remounted),
    // but the caret would be lost. Restore focus after the reorder so the user can
    // keep typing straight away — the "still writable after moving" requirement.
    const focusedAtStart = document.activeElement as HTMLElement | null;
    const restoreFocus = () => {
      if (
        focusedAtStart &&
        focusedAtStart !== document.body &&
        focusedAtStart.isConnected &&
        document.activeElement !== focusedAtStart
      ) {
        focusedAtStart.focus({ preventScroll: true });
      }
    };

    // Insertion caret: an accent bar on the boundary tab, moved by class only
    // when the target slot changes (O(1) per change — no per-frame DOM churn).
    const clearCaret = () => {
      if (caretEl) {
        caretEl.classList.remove('wp-tab--drop-before', 'wp-tab--drop-after');
        caretEl = null;
      }
    };
    const showCaret = (index: number) => {
      const next = index < others.length ? others[index]! : (others[others.length - 1] ?? null);
      const cls = index < others.length ? 'wp-tab--drop-before' : 'wp-tab--drop-after';
      if (next === caretEl && next?.classList.contains(cls)) return;
      clearCaret();
      caretEl = next;
      next?.classList.add(cls);
    };

    const cleanup = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
      window.removeEventListener('keydown', onKey);
      tabEl.classList.remove('wp-tab--dragging');
      tabEl.style.transform = '';
      clearCaret();
    };

    const onMove = (ev: PointerEvent) => {
      if (mode === 'detach') return; // already handed off to the float drag
      const dx = ev.clientX - startX;
      const next = tabDragMode(dx, bandExcess(ev.clientY, bandTop, bandBottom));
      if (next === 'detach') {
        mode = 'detach';
        cleanup();
        startPanelDrag(panelId, ev, { fromTab: true });
        return;
      }
      if (next === 'reorder') {
        mode = 'reorder';
        tabEl.classList.add('wp-tab--dragging');
        // Direct transform + caret — cheap math against the precomputed centres,
        // no live reorder, no store write.
        tabEl.style.transform = `translate3d(${dx}px, 0, 0)`;
        dropIndex = targetTabIndex(ev.clientX, otherCentres);
        showCaret(dropIndex);
      }
    };

    const onUp = () => {
      const finalMode = mode;
      const index = dropIndex;
      cleanup();
      if (finalMode === 'idle') {
        props.api.setActive(); // a plain click: activate the panel
      } else if (finalMode === 'reorder') {
        workspace.reorderTab(panelId, index); // commit exactly once, on drop
        restoreFocus(); // keep typing after the move
      }
    };

    // Escape / a system pointer-cancel abort cleanly — nothing was committed
    // mid-drag, so there is no live reorder to undo, just teardown. (A detach
    // hands off to startPanelDrag, which has its own cancel handling.)
    const abort = () => cleanup();
    const onCancel = () => abort();
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') abort();
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    window.addEventListener('keydown', onKey);
  };

  return (
    <div className="wp-tab" data-panel-id={panelId} onPointerDown={onPointerDown} title={String(title)}>
      <span className="wp-tab__title">{title}</span>
      <Tip label="Close">
        <button
          data-no-drag
          className="wp-tab__close"
          aria-label="Close"
          onClick={() => workspace.closePanel(panelId)}
        >
          <X size={11} />
        </button>
      </Tip>
    </div>
  );
}

/**
 * Trailing tab-strip action: close everything at once.
 *
 * It lives at the END of the strip rather than in a menu because that is where
 * a strip's own affordances belong, and because a fixed position means it never
 * moves as tabs open and close — the same reason the tabs themselves are one
 * width. Hidden below two tabs, where "close all" and the tab's own × are the
 * same gesture.
 */
function StripActions() {
  const count = useWorkspaceStore((s) => s.tabOrder.length);
  if (count < 2) return null;
  // LABELLED, and set off by a rule. A bare glyph at the end of a tab strip is
  // one mis-tap from wiping the session, and it read as a per-tab close because
  // it was the same weight and the same shape as one. The count is in the label
  // so the blast radius is on screen before the click, not in a tooltip.
  // Dockview stretches its right-actions slot to the full strip height, so a
  // control with its own height sat flush against the top edge. This wrapper
  // centres it in the strip instead of letting it hang from the top.
  return (
    <div className="wp-strip-actions">
      <span className="wp-strip-divider" aria-hidden="true" />
      <Tip label={`Close all ${count} tabs`}>
        <button
          className="wp-strip-action wp-strip-action--labelled"
          aria-label={`Close all ${count} tabs`}
          onClick={() => workspace.closeAll()}
        >
          <XSquare size={12} />
          <span>Close all {count}</span>
        </button>
      </Tip>
    </div>
  );
}

const components = {
  workspacePanel: (props: IDockviewPanelProps) => {
    const panelId = (props.params as { panelId?: string }).panelId ?? props.api.id;
    return (
      <div className="wp-tabbed-slot">
        <div className="wp-card wp-card--tabbed" data-panel-id={panelId}>
          <PanelBody panelId={panelId} />
        </div>
      </div>
    );
  },
};

/**
 * Dockview drives the tabbed dock (the browser tab strip). Native DnD/floating
 * are disabled; the `workspace` controller + `startPanelDrag` own all movement,
 * so tab drag-out and drag-in are custom and precise.
 */
export function DockLayer() {
  const handleReady = (event: DockviewReadyEvent) => {
    setDockApi(event.api);
    workspace.bind(event.api);
  };

  return (
    <DockviewReact
      className="wp-dock"
      theme={themeAbyss}
      disableDnd
      disableFloatingGroups
      components={components}
      defaultTabComponent={CustomTab}
      rightHeaderActionsComponent={StripActions}
      onReady={handleReady}
    />
  );
}
